import React, { useContext, useState } from 'react';
import { useEffect } from 'react';
import { PeFilterContext } from '../../Contexts/PeFilterContext';
import MultiSelectEntity from '../RefineSearch/MultiSelectEntity';
import Popup from '../Common/Popup';

/**
 * The `FirmTypeFilter` function is a React component that renders a filter for selecting firm types,
 * with options that can be expanded or collapsed.
 * @returns The `FirmTypeFilter` component is returning a JSX fragment containing a `div` element with
 * a class of "border-b-[2px] border-[#E8E6E1]". Inside the `div`, there is a `li` element with a
 * conditional class name based on the value of the `open` prop. Inside the `li` element, there is a `
 */
function FirmTypeFilter({ FundsIndustryOptions, setFundsIndustryOptions, customPathname, label = "Sector", isPopupOpen, setIsPopupOpen }) {
    const { FundsIndustry, refineSearchFilters } = useContext(PeFilterContext);
    
    // Use internal state only if props are not provided (for backward compatibility)
    const [internalPopupOpen, setInternalPopupOpen] = useState(false);
    const popupOpen = isPopupOpen !== undefined ? isPopupOpen : internalPopupOpen;
    const setPopupOpen = setIsPopupOpen !== undefined ? setIsPopupOpen : setInternalPopupOpen;

    /* The `useEffect` hook in this code is used to perform side effects in a React functional
    component. In this case, it is used to update the `FundsIndustryOptions` state based on the
    values of `FundsIndustry`, `customPathname`, `refineSearchFilters`, and
    `setFundsIndustryOptions`. */
    useEffect(() => {
        const industryIds = refineSearchFilters[customPathname]?.fundIndustry?.map(value => value.id) ?? [];
        setFundsIndustryOptions(FundsIndustry?.map(industry => ({ ...industry, isChecked: industryIds.includes(industry.id) })))
    }, [FundsIndustry, customPathname, refineSearchFilters, setFundsIndustryOptions])

    /* The `handleFundTypeChange` function is an event handler that is triggered when the user selects
    or deselects a firm type option in the filter. It receives the event object `e` as a parameter. */
    const handleFundTypeChange = (e) => {
        const { name, checked, id, filteredIds } = e.target;
        let currentFundType = [];
    
        if (name === "allSelect") {
            currentFundType = FundsIndustryOptions.map((fundtype) =>
                ({ ...fundtype, isChecked: checked })
            );
        } else if (name === "filteredSelect" && filteredIds) {
            currentFundType = FundsIndustryOptions.map((fundtype) => {
                if (filteredIds.includes(fundtype.id)) {
                    return { ...fundtype, isChecked: checked };
                }
                return fundtype;
            });
        } else {
            currentFundType = FundsIndustryOptions.map((fundtype) =>
                fundtype.id === parseInt(id) ? { ...fundtype, isChecked: checked } : fundtype
            );
        }
    
        setFundsIndustryOptions(currentFundType);
    };

    const selectedLength = FundsIndustryOptions?.filter(option => option.isChecked)?.length ?? 0;

    return (
        <div className="relative w-full md:inline-block">
            <button
                type="button"
                className="h-10 rounded-md px-4 py-1 text-gray-500 hover:bg-gray-100 text-sm shadow-md border border-gray-300 text-left flex items-center justify-between w-full md:w-[90%]"
                onClick={() => setPopupOpen(!popupOpen)}
            >
                <span className="truncate pr-2">{label} <span className="opacity-60 ml-2 text-xs text-ellipsis">e.g. {FundsIndustry.slice(0,2).map(item => item.name).join(', ')}</span></span>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {selectedLength > 0 && (
                        <span className="bg-white rounded-full px-2 py-0.5 text-xs font-medium border border-gray-400">
                            {selectedLength}
                        </span>
                    )}
                    <span className={`transition-transform duration-200 text-xl font-semibold ${popupOpen ? 'rotate-[270deg]' : 'rotate-90'}`}>
                        ›
                    </span>
                </div>
            </button>

            {popupOpen && (
                <Popup onClose={() => setPopupOpen(false)}>
                    <div className="p-2">
                        <MultiSelectEntity
                            handleEntityChange={handleFundTypeChange}
                            primaryEntity={FundsIndustryOptions}
                        />
                    </div>
                </Popup>
            )}
        </div>
    );
}

export default FirmTypeFilter;